import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Reveal from "@/components/Reveal";
import { FALLBACK_CATALOG, scrollToSection } from "@/data/catalog";
import { FaInstagram, FaTiktok, FaYoutube, FaSpotify, FaFacebookF, FaSoundcloud, FaPinterestP, FaSnapchatGhost, FaTwitter } from "react-icons/fa";
import { Zap, ArrowUpRight, Globe, Music2, AtSign, Radio } from "lucide-react";

const API = "/api";

const ICONS = {
  instagram: FaInstagram,
  tiktok: FaTiktok,
  youtube: FaYoutube,
  spotify: FaSpotify,
  facebook: FaFacebookF,
  soundcloud: FaSoundcloud,
  pinterest: FaPinterestP,
  snapchat: FaSnapchatGhost,
  twitter: FaTwitter,
  website: Globe,
  threads: AtSign,
  clubhouse: Radio,
  audiomack: Music2,
  tidal: Music2,
  datpiff: Music2,
  reverbnation: Music2,
};

export default function Pricing() {
  const [catalog, setCatalog] = useState(FALLBACK_CATALOG);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    axios
      .get(`${API}/catalog`)
      .then((res) => {
        if (res.data?.platforms?.length) setCatalog(res.data);
      })
      .catch(() => {});
  }, []);

  const platforms = filter === "all" ? catalog.platforms : catalog.platforms.filter((p) => p.slug === filter);
  const loading = catalog.platforms.length === 0;

  return (
    <section id="pricing" data-testid="pricing-section" className="relative border-t border-[#1F1F1F] bg-[#050505] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#E11D2E]">Packages & pricing</p>
            <h2 className="mt-4 font-display text-4xl uppercase leading-[1.02] tracking-tight text-white sm:text-5xl">
              The catalog.
              <span className="block text-[#E11D2E]">Exact numbers.</span>
            </h2>
          </div>
          <p className="max-w-sm font-mono text-xs leading-relaxed text-[#8A8A8A]">
            ALL PRICES IN USD, REVEALED AT ORDER. VOLUME AND TARGETED PRICING ON REQUEST.
          </p>
        </Reveal>

        {!loading && (
          <Reveal delay={0.1} className="mt-10 flex flex-wrap gap-2" data-testid="platform-filters">
            <button
              data-testid="filter-all"
              onClick={() => setFilter("all")}
              className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.14em] transition-colors ${filter === "all" ? "bg-[#E11D2E] text-white" : "border border-[#1F1F1F] text-[#8A8A8A] hover:text-white"}`}
            >
              All
            </button>
            {catalog.platforms.map((p) => (
              <button
                key={p.slug}
                data-testid={`filter-${p.slug}`}
                onClick={() => setFilter(p.slug)}
                className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.14em] transition-colors ${filter === p.slug ? "bg-[#E11D2E] text-white" : "border border-[#1F1F1F] text-[#8A8A8A] hover:text-white"}`}
              >
                {p.name}
              </button>
            ))}
          </Reveal>
        )}

        {loading && (
          <p className="mt-16 font-mono text-xs uppercase tracking-[0.2em] text-[#555555]" data-testid="catalog-loading">
            Loading catalog…
          </p>
        )}

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {platforms.map((platform, i) => {
            const Icon = ICONS[platform.slug] || Zap;
            if (platform.coming_soon) {
              return (
                <Reveal key={platform.slug} delay={(i % 3) * 0.08} data-testid={`pricing-card-${platform.slug}`}>
                  <div className="flex h-full flex-col items-center justify-center border border-dashed border-[#333333] bg-[#0D0D0D]/50 p-10 text-center">
                    <Icon size={22} className="text-[#555555]" />
                    <h3 className="mt-4 font-display text-base uppercase tracking-wide text-[#8A8A8A]">{platform.name}</h3>
                    <span className="mt-3 border border-[#E11D2E]/50 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#E11D2E]">
                      Coming soon
                    </span>
                    <p className="mt-4 font-mono text-[11px] text-[#555555]">In the lab. Dropping soon.</p>
                  </div>
                </Reveal>
              );
            }
            return (
              <Reveal key={platform.slug} delay={(i % 3) * 0.08} data-testid={`pricing-card-${platform.slug}`}>
                <div className="group flex h-full flex-col border border-[#1F1F1F] bg-[#0D0D0D] transition-all duration-500 hover:border-[#333333] hover:shadow-[0_0_48px_rgba(225,29,46,0.1)]">
                  <div className="flex items-center justify-between border-b border-[#1F1F1F] p-5">
                    <div className="flex items-center gap-3">
                      {platform.icon_url ? (
                        <img src={platform.icon_url} alt="" className="h-5 w-5 object-contain" />
                      ) : (
                        <Icon size={18} className="text-white transition-colors duration-300 group-hover:text-[#E11D2E]" />
                      )}
                      <Link
                        to={`/services/${platform.slug}`}
                        data-testid={`platform-link-${platform.slug}`}
                        className="font-display text-base uppercase tracking-wide text-white transition-colors hover:text-[#E11D2E]"
                      >
                        {platform.name}
                      </Link>
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A]">
                      {String(platform.services.length).padStart(2, "0")} services
                    </span>
                  </div>
                  <div className="flex-1">
                    {platform.services.map((s) => (
                      <div
                        key={s.id}
                        data-testid={`service-row-${s.id}`}
                        className="flex items-center justify-between gap-3 border-b border-[#1F1F1F]/60 px-5 py-3.5 last:border-0"
                      >
                        <div className="flex flex-wrap items-baseline gap-x-2.5">
                          <span className="text-sm font-600 text-white">{s.name}</span>
                          <span className="font-mono text-[11px] text-[#E11D2E]" data-testid={`service-qty-${s.id}`}>
                            {s.quantity.toLocaleString("en-US")} {s.unit}
                          </span>
                        </div>
                        <Link
                          to={`/order/${s.id}`}
                          data-testid={`order-btn-${s.id}`}
                          aria-label={`Get price for ${s.name}`}
                          className="flex shrink-0 items-center gap-1 border border-[#1F1F1F] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A] transition-all duration-300 hover:border-[#E11D2E] hover:text-white"
                        >
                          Get price <ArrowUpRight size={11} />
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            );
          })}

          {!loading && filter === "all" && (
            <Reveal delay={0.2} data-testid="pricing-bundles-card" id="packages">
              <div className="flex h-full flex-col border border-[#E11D2E]/40 bg-[#0D0D0D] p-6 shadow-[0_0_48px_rgba(225,29,46,0.08)]">
                <div className="flex items-center justify-between border-b border-[#1F1F1F] pb-6">
                  <h3 className="font-display text-base uppercase tracking-wide text-white">Targeted bundles</h3>
                  <span className="bg-[#E11D2E] px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white">Hot</span>
                </div>
                <div className="flex-1">
                  {catalog.bundles.map((b) => (
                    <div
                      key={b.id}
                      data-testid={`bundle-row-${b.id}`}
                      className="flex items-center justify-between gap-4 border-b border-[#1F1F1F]/60 py-4"
                    >
                      <div>
                        <div className="text-sm font-600 text-white">{b.name}</div>
                        <div className="mt-0.5 font-mono text-[11px] uppercase tracking-[0.14em] text-[#E11D2E]">{b.tag}</div>
                      </div>
                      <Link
                        to={`/order/${b.id}`}
                        data-testid={`order-btn-${b.id}`}
                        aria-label={`Get price for ${b.name}`}
                        className="flex shrink-0 items-center gap-1 border border-[#1F1F1F] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A] transition-all duration-300 hover:border-[#E11D2E] hover:text-white"
                      >
                        Get price <ArrowUpRight size={11} />
                      </Link>
                    </div>
                  ))}
                </div>
                <p className="pt-4 font-mono text-[11px] leading-relaxed text-[#8A8A8A]">
                  FREE TO TEST OUR SERVICES. INCREDIBLY CHEAP & EFFECTIVE.
                </p>
                <button
                  data-testid="bundles-cta"
                  onClick={() => scrollToSection("#contact")}
                  className="mt-6 w-full border border-[#E11D2E] py-3.5 font-mono text-xs font-600 uppercase tracking-[0.14em] text-[#E11D2E] transition-all duration-300 hover:bg-[#E11D2E] hover:text-white"
                >
                  Request a custom bundle
                </button>
              </div>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}
